import { MessageSquare, Video } from 'lucide-react';
import type { MouseEvent, SyntheticEvent } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  OPEN_PROJECT_CALL_EVENT,
  type OpenProjectCallDetail,
} from '@/components/chat-dock/project-call-actions';

export interface ProjectCommunicationActionsProps {
  projectId: string | null;
  projectName?: string | null;
  className?: string;
}

/**
 * Compact project-level communication shortcuts for rows and headers.
 *
 * The dock owns channel/call authorization and emits the regular device-lobby
 * call flow. This component intentionally only carries the explicit project
 * identifier across that boundary.
 */
export function ProjectCommunicationActions({
  projectId,
  projectName,
  className,
}: ProjectCommunicationActionsProps) {
  if (!projectId) return null;

  const projectLabel = projectName ? `${projectId} — ${projectName}` : projectId;

  const stopRowEvent = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const openVideoCall = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent<OpenProjectCallDetail>(OPEN_PROJECT_CALL_EVENT, {
      detail: { projectId, type: 'video' },
    }));
  };

  const openChat = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent('open-project-channel', {
      detail: { projectId },
    }));
  };

  return (
    <div className={cn('ml-auto flex items-center justify-end gap-1 shrink-0', className)}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        aria-label={`Video call for ${projectId}`}
        title={`Start a video call for ${projectLabel}`}
        onPointerDown={stopRowEvent}
        onClick={openVideoCall}
      >
        <Video className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        aria-label={`Open chat for ${projectId}`}
        title={`Open chat for ${projectLabel}`}
        onPointerDown={stopRowEvent}
        onClick={openChat}
      >
        <MessageSquare className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}